import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getTours } from '../services/api';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { imgErrorFallback } from '../utils/imageFallback';
import WishlistButton from './WishlistButton';

const formatPrice = (price) => {
  return new Intl.NumberFormat('tr-TR').format(price) + ' ₺';
};

const TourCard = ({ tour }) => (
  <div className="relative flex-shrink-0 w-[260px] md:w-[280px]">
    <WishlistButton tourId={tour.id} tourTitle={tour.title} />
    <Link
      to={`/tur/${tour.slug}`}
      className="group/card block cursor-pointer"
    >
      <div className="relative w-full aspect-[4/3] rounded-xl overflow-hidden mb-3">
        <img
          src={tour.heroImage}
          alt={tour.heroImageAlt || tour.title}
          onError={imgErrorFallback}
          className="w-full h-full object-cover transition-transform duration-500 group-hover/card:scale-105"
          loading="lazy"
        />
        {tour.onSale && (
          <span className="absolute top-3 left-3 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">İndirim</span>
        )}
        {tour.levelName && (
          <span className="absolute bottom-3 right-3 bg-white/95 backdrop-blur text-[#1B1B1B] text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm">
            Sv. {tour.level} · {tour.levelName}
          </span>
        )}
      </div>
      <div>
        <h3 className="text-[15px] font-bold text-[#1B1B1B] mb-1.5 leading-tight line-clamp-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          {tour.title}
        </h3>
        <p className="text-sm text-gray-500 mb-1.5">{tour.nights} gece &middot; {tour.location}</p>
        <div className="flex items-center gap-2 mb-2">
          {tour.onSale && tour.originalPrice && (
            <span className="text-sm text-gray-400 line-through">{tour.currency}{formatPrice(tour.originalPrice)}</span>
          )}
          <span className="text-base font-bold text-[#1B1B1B]">{tour.currency}{formatPrice(tour.salePrice)}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500 font-medium">Seviye {tour.level} - {tour.levelName}</span>
          <div className="flex items-center gap-1">
            <Star size={12} fill="#C8D843" stroke="#C8D843" />
            <span className="text-xs font-semibold text-[#1B1B1B]">{tour.rating || 0}</span>
            <span className="text-xs text-gray-400">({tour.reviewCount || 0})</span>
          </div>
        </div>
      </div>
    </Link>
  </div>
);

const TourSection = ({ title, subtitle, tours, scrollRef, bgColor = 'bg-white' }) => {
  const scroll = (dir) => {
    if (scrollRef.current) {
      const amount = dir === 'left' ? -320 : 320;
      scrollRef.current.scrollBy({ left: amount, behavior: 'smooth' });
    }
  };

  if (!tours || tours.length === 0) return null;

  return (
    <section className={`py-12 md:py-16 ${bgColor}`}>
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h2 
            className="text-3xl md:text-4xl font-bold text-[#1B1B1B] mb-2"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {title}
          </h2>
          <p className="text-gray-600 text-base">{subtitle}</p>
        </div>

        <div className="relative group">
          <button
            onClick={() => scroll('left')}
            className="absolute -left-4 top-1/3 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-gray-50"
          >
            <ChevronLeft size={20} />
          </button>

          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scroll-smooth pb-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {tours.map((tour) => (
              <TourCard key={tour.id} tour={tour} />
            ))}
          </div>

          <button
            onClick={() => scroll('right')}
            className="absolute -right-4 top-1/3 -translate-y-1/2 z-20 bg-white shadow-lg rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-gray-50"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

const FeaturedTrips = () => {
  const [mostPopular, setMostPopular] = useState([]);
  const [kariaTours, setKariaTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const popularScrollRef = useRef(null);
  const kariaScrollRef = useRef(null);

  useEffect(() => {
    const fetchTours = async () => {
      try {
        const res = await getTours({ limit: 20 });
        const allTours = res.data || [];
        
        // Önce 3 gece turları (Fethiye, Kaş, Gelidonya sırasıyla)
        const threeNight = allTours.filter(t => t.nights === 3 || t.salePrice === 18000);
        const fourNight = allTours.filter(t => t.nights === 4 && t.salePrice !== 18000);
        
        const tourOrder = ['fethiye', 'kas', 'gelidonya'];
        const sortByOrder = (tours) => tours.sort((a, b) => {
          const indexA = tourOrder.findIndex(k => a.slug.toLowerCase().includes(k));
          const indexB = tourOrder.findIndex(k => b.slug.toLowerCase().includes(k));
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return (b.rating || 0) - (a.rating || 0);
        });
        
        const sorted3Night = sortByOrder(threeNight);
        const sorted4Night = sortByOrder(fourNight);
        
        // En çok beğenilenler: 3 gece turları önce, sonra 4 gece
        const popular = [...sorted3Night, ...sorted4Night].slice(0, 6);
        
        // Karia turları (Datça)
        const karia = allTours.filter(t => t.slug.toLowerCase().includes('karia') || t.slug.toLowerCase().includes('datca'));
        
        setMostPopular(popular);
        setKariaTours(karia);
      } catch (err) {
        console.error('Tours fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTours();
  }, []);

  if (loading) return <div className="py-12 text-center text-gray-500">Yükleniyor...</div>;

  return (
    <>
      {/* En Çok Beğenilenler */}
      <TourSection 
        title="En Çok Beğenilenler"
        subtitle="Maceraperestlerin favorileri"
        tours={mostPopular}
        scrollRef={popularScrollRef}
        bgColor="bg-[#f9f9f6]"
      />

      {/* Karia Yolu */}
      {kariaTours.length > 0 && (
        <TourSection 
          title="Karia Yolu'nu Keşfedin"
          subtitle="Datça Yarımadası'nın eşsiz güzelliğini keşfedin"
          tours={kariaTours}
          scrollRef={kariaScrollRef}
          bgColor="bg-white"
        />
      )}
    </>
  );
};

export default FeaturedTrips;
